import { cloneDeep, isArray, isObject, isString } from 'lodash'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { Operator } from 'src/libs/constant'

const LOADING = 'loading'
const SUCCESS = 'success'
const ERROR = 'error'
const IDLE = 'idle'

function buildArgs (defaultArgs, inputArgs) {
  const _inputArgs = inputArgs || defaultArgs
  if (_inputArgs) {
    if (isString(_inputArgs) || (isObject(_inputArgs) && !isArray(_inputArgs))) return [_inputArgs]
    if (isArray(_inputArgs)) return _inputArgs
  }
  return []
}

function replacePaginateItemContent (originData, updateData, mapKeys) {
  const mergedData = {
    ...originData,
    ...updateData
  }

  if (!mapKeys || !mapKeys.length) {
    return mergedData
  }

  const newOriginData = cloneDeep(mergedData)

  mapKeys.forEach((mapKey) => {
    if (mapKey && mapKey.length === 2) {
      newOriginData[mapKey[0]] = updateData[mapKey[1]]
    }
  })

  return newOriginData
}

export function usePaginationFilter (listRequest, criteria, effect, options) { // effect must return promise that resolve new data, does not mutate data pass to it
  const { isDisabled = false, signature: requestSignature, queryString, isAutoReload = false, paginate, studyProgramId, studentId } = (options || {})
  const [{ paginatedData, status, statusCode, signature, paginateUpdatedAt }, setPaginatedData] = useState({ status: isDisabled ? IDLE : LOADING, signature: undefined, paginateUpdatedAt: 1 })
  // const [paginateUpdatedAt, setPaginateUpdateAt] = useState(0)
  const [baseData, setBaseData] = useState()
  const [reloadKey, setReloadKey] = useState()
  const router = useRouter()

  let page;
  let size;
  if (paginate) {
    page = paginate.page;
    size = paginate.size;    
  } else {
    page = router.query.page;
    size = router.query.size;
  } 
  page && (page = Number(page - 1))
  size && (size = Number(size))

  const isValidPageSize = typeof page === 'number' && typeof size === 'number'

  useEffect(() => {
    if (isValidPageSize && page >= 0 && size > 0 && !isDisabled) {
      search()
    }
  }, [criteria, page, size, reloadKey, isDisabled, JSON.stringify(queryString)])

  useEffect(() => {
    if (baseData?.content && effect) {      
      effect(baseData)
        .then(newData => { // sometime, must call extra request to get more info
          if (newData !== baseData) {
            setPaginatedData({ paginatedData: newData, status: SUCCESS, paginateUpdatedAt: Date.now() })
          }
        })
    }
  }, [baseData])

  async function search () {
    setPaginatedData(prevPaginateData => ({ ...prevPaginateData, paginatedData, status: LOADING }))
    const payload = {}
    const keys = ['page', 'size', 'criteria'];
    [page, size, criteria].forEach((val, idx) => {
      if (val === 0 || val) payload[keys[idx]] = val
    })
    const { data, status } = await listRequest({...payload, studyProgramId, studentId}, queryString)
    if (status === 200 || status === 400) {
      /**
       * Khi filter values không hợp lệ, backend sẽ response về status = 400.
       * Ở component auth\RouteValidator thực hiện xử lý status = 400 sẽ render ra page 404
       * Vì vậy, hardcode defaultFillterData và status khi backend response status 400 ở đây để không hiển thị page 404
       * Thay vào đó response status 400 sẽ được xử lý như việc filter về không có kết quả.
       */
      const defaultFilterData = {
        content: [],
        page: 1,
        size: 1
      }
      const statusCode = status === 400 ? 200 : status
      if (!effect) {
        setPaginatedData({ paginatedData: data || defaultFilterData, status: SUCCESS, statusCode, signature: requestSignature, paginateUpdatedAt: Date.now() })
      } else {
        setBaseData(data || defaultFilterData)
      }
    } else {
      setPaginatedData(prevPaginateData => ({ ...prevPaginateData, paginatedData: null, status: ERROR, statusCode: status }))
    }
  }
  useEffect(() => {
    let itv
    if (paginatedData && isAutoReload) {
      itv = setInterval(() => {
        reload()
      }, 300000)
    }
    return () => {
      if (itv) {
        clearInterval(itv)
      }
    }
  }, [paginatedData])

  function reload () {
    setReloadKey(Date.now())
  }

  function clear() {
    setPaginatedData({ status: IDLE, signature: undefined, paginateUpdatedAt: 1 })
    setBaseData(null)
  }

  /**
   *
   * @param {Promise} reloadApi - axios request API
   * @param {Array} defaultApiArgs - tham số mặc định truyền vào API
   * @param {Array} mapKeys - Key map giữa response data từ reloadApi và data đang hiển thị để update
   * @returns
   */
  const updateMultiRows = (reloadApi, defaultApiArgs = [], mapKeys = []) => {
    if (!reloadApi) {
      throw Error('reloadApi is required, you need set it!')
    }
    /**
     * @param {String} identifier - Tên ID của data muốn update được map với response reloadApi
     * @param {Array} payloads - Mảng chứa params thông tin update
     *
     */
    return async (identifier = 'id', payloads = []) => {
      const isMultiPayloads = isArray(payloads) && payloads.length > 1
      let listReloadsApi = []
      if (isMultiPayloads) {
        listReloadsApi = payloads.map(payload => {
          const requestArgs = buildArgs(defaultApiArgs, payload)
          return reloadApi.apply(this, requestArgs)
        })
      } else {
        const requestArgs = buildArgs(defaultApiArgs, payloads)
        listReloadsApi = [reloadApi.apply(this, requestArgs)]
      }

      if (!listReloadsApi.length) return

      await Promise.all(listReloadsApi).then(async (results) => {
        const newPaginatedData = { ...paginatedData }
        const newContent = [...(paginatedData.content || [])]
        for (const resultIndex in results) {
          const result = results[resultIndex]?.data
          const contentIndex = result ? newContent.findIndex(item => item[identifier] === result[identifier]) : -1
          if (contentIndex < 0) continue
          if (results[resultIndex]?.data) {
            const result = results[resultIndex]?.data
            if (effect && baseData) {
              const rowBaseData = {
                ...baseData,
                content: [result]
              }
              const newRowBaseData = await effect(rowBaseData)
              if (newRowBaseData !== rowBaseData) {
                newContent.splice(contentIndex, 1, replacePaginateItemContent(newContent[contentIndex], newRowBaseData.content[0], mapKeys))
              }
            } else {
              newContent.splice(contentIndex, 1, replacePaginateItemContent(newContent[contentIndex], result, mapKeys))
            }
          }
        }
        newPaginatedData.content = newContent
        setPaginatedData(prevPaginateData => ({ ...prevPaginateData, paginatedData: newPaginatedData, status: SUCCESS }))
      }).catch((err) => {
        console.error(err)
      })
    }
  }

  return { paginatedData, status, statusCode, updateMultiRows, reload, clear, signature, paginateUpdatedAt }
}

export function checkAndCorrectiveOrMultipleField (
  fieldName,
  actualFields,
  criteria
) {
  const generalCriteriaItem = criteria.find((item) => item.field === fieldName)
  if (generalCriteriaItem) {
    const { value, values, operator } = generalCriteriaItem || {}
    const childList = Array.from(actualFields).map((field) => {
      const criteriaItem = {
        field,
        operator: operator,
        value,
        values
      }
      return criteriaItem
    })
    criteria = criteria
      .concat({
        operator: Operator.OR_MULTIPLE_FIELDS,
        childList
      })
      .filter((item) => item !== generalCriteriaItem)
  }

  return criteria
}
